import { Article } from '@wix/answers-api';
import { pageClick } from '@wix/bi-logger-new-help-center/v2';
import { Anchors, ConsumerSource } from '@wix/cc-user-facing-common-components';
import { Box, Text } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { animateScroll } from 'react-scroll';
import { ArrowLeft } from 'wix-ui-icons-common';

import { BI } from '../../constants';
import { DATA_HOOKS } from '../../dataHooks';
import { useBI } from '../../hooks/useBI';
import { Step } from '../../pages/article/[articleTitle]';
import { PanelAction, PanelType } from '../../types';
import { useArticlePageBi } from '../../utils/article-page-bi';
import { mapArticleTypeToItemType } from '../../utils/bi';

import css from './index.module.scss';

type ArticleLeftSideBarProps = {
  steps: Step[];
  article: Article;
  showLeftSideBar: boolean;
  setShowLeftSideBar: (showLeftSideBar: boolean) => void;
  copilotArticleContentRef: React.RefObject<HTMLDivElement>;
  anchorsRef: React.RefObject<HTMLDivElement>;
};

export const ArticleLeftSideBar = ({
  steps,
  article,
  showLeftSideBar,
  setShowLeftSideBar,
  copilotArticleContentRef,
  anchorsRef,
}: ArticleLeftSideBarProps) => {
  const { locale } = useRouter();
  const { sendBIEvent } = useBI();
  const { t } = useTranslation();
  const { panelTrack } = useArticlePageBi(article);
  const [prevShowState, setPrevShowState] = useState(showLeftSideBar);
  const [shouldAnimate, setShouldAnimate] = useState(false);

  useEffect(() => {
    if (prevShowState !== showLeftSideBar) {
      setShouldAnimate(true);
      setPrevShowState(showLeftSideBar);
    }
  }, [showLeftSideBar, prevShowState]);

  const onAnchorClick = async (text: string, order: string) => {
    animateScroll.scrollToTop();
    await sendBIEvent(
      pageClick({
        source_name: BI.SOURCE_NAMES.ARTICLE,
        kb_lang: locale as string,
        item_id: article.id,
        item_type: mapArticleTypeToItemType(article.type),
        clicked_item_type: BI.CLICKED_ITEM_TYPES.SIDEBAR_ANCHOR,
        clicked_text: text,
        clicked_item_order: order,
        is_page_content_click: true,
      })
    );
  };

  const onCloseClick = async () => {
    await panelTrack(PanelType.ANCHORS, PanelAction.COLLAPSE);
    setShowLeftSideBar(false);
  };

  return (
    <Box
      className={classNames(css.wrapper, {
        [css.showLeftSideBar]: shouldAnimate && showLeftSideBar,
        [css.hideLeftSideBar]: shouldAnimate && !showLeftSideBar,
        [css.staticHidden]: !shouldAnimate && !showLeftSideBar,
      })}
      direction="vertical"
      gap={2}
      ref={anchorsRef}
    >
      <Box direction="vertical" gap={2}>
        <Box className={css.titleWrapper}>
          <div
            className={css.toggleButton}
            data-hook={DATA_HOOKS.ARTICLE_LEFT_SIDEBAR_TOGGLE_BUTTON}
            onClick={onCloseClick}
          >
            {<ArrowLeft />}
          </div>
          <Text weight="bold" className={css.title}>
            {t('scrollspy.title')}
          </Text>
        </Box>
        <Box>
          <Anchors
            steps={steps}
            onClick={onAnchorClick}
            hideTitle={true}
            locale={locale as string}
            consumerSource={ConsumerSource.HELP_CENTER}
            showScrollToTop={false}
            containerRef={copilotArticleContentRef}
          />
        </Box>
      </Box>
    </Box>
  );
};
